import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit
} from '@angular/core';
import { combineLatest, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { KeyValuePipe } from '@angular/common';
import {
  SdAccordionComponent,
  SdAccordionGroupComponent,
  SdLoaderComponent,
  ThemesTextEnum
} from '@seb/web-design-system/core';
import { BaseFaqComponent } from '@seb/web-common/core/components/base/base-faq/base-faq.component';
import { FaqEntry, FaqJsonLd, FaqSearchResult, GlobalSearchResult } from '@seb/web-common/core/models';
import { GlobalSearchService, TrackingService } from '@seb/web-common/core/services';
import { areQueryParamMapEqual } from '@seb/web-common/core/utils';
import { CustomJsonLdDirective } from '@seb/web-common/core/directives';
import { FAQMapper } from '@seb/web-common/core/mappers';

@Component({
  selector: 'wc-faq-search-list',
  templateUrl: './faq-search-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SdLoaderComponent,
    SdAccordionGroupComponent,
    SdAccordionComponent,
    BaseFaqComponent,
    KeyValuePipe,
    CustomJsonLdDirective
  ]
})
export class FaqSearchListComponent implements OnInit, OnDestroy {
  @Input() totalFaqs: number;

  areFaqsLoading = false;
  faqsByCategory: Map<string, FaqEntry[]>;
  openedFaqs: boolean[];
  themesTextEnum = ThemesTextEnum;
  jsonLdFaqs?: FaqJsonLd;

  private destroy$: Subject<void> = new Subject();

  constructor(
    private globalSearchService: GlobalSearchService,
    private route: ActivatedRoute,
    private trackingService: TrackingService,
    private faqMapper: FAQMapper,
    private cdRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.route.queryParamMap
      .pipe(
        distinctUntilChanged((previous, current) => areQueryParamMapEqual(previous, current, 'query')),
        takeUntil(this.destroy$)
      )
      .subscribe((params) => this.searchFaqs(params));
  }

  onOpened(toggle: boolean, index: number, categoryName: string): void {
    this.openedFaqs[index] = !toggle;
    this.cdRef.markForCheck();
    if (!toggle) {
      this.trackingService.sendEvent('FAQ', { faqTopic: categoryName });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private searchFaqs(queryParams: ParamMap): void {
    if (this.totalFaqs > 0) {
      this.areFaqsLoading = true;
      const pageSize = 100;
      const nbPages = Math.ceil(this.totalFaqs / pageSize);
      const search$: Observable<GlobalSearchResult>[] = [];
      for (let i = 0; i < nbPages; i++) {
        search$.push(
          this.globalSearchService.globalSearch({
            query: queryParams.get('query'),
            isAccessorySearch: queryParams.has('isAccessorySearch') ? queryParams.get('isAccessorySearch') : false,
            target: 'faq',
            pageSize,
            currentPage: i
          })
        );
      }
      combineLatest(search$)
        .pipe(
          map((globalResults) =>
            globalResults
              .map((globalResult) => globalResult.faqSearchPageData.faqSearchResult)
              .reduce((previousValue, currentValue) => [...previousValue, ...currentValue])
          )
        )
        .subscribe((faqSearchResults) => {
          this.jsonLdFaqs = this.faqMapper.toJsonLdFaqs(faqSearchResults.map(({ faq }) => faq));
          this.faqsByCategory = this.sortFaqsByCategory(faqSearchResults);
          this.openedFaqs = new Array(this.faqsByCategory.size).fill(false);
          this.areFaqsLoading = false;
          this.cdRef.markForCheck();
        });
    } else {
      this.faqsByCategory = new Map();
      this.openedFaqs = [];
      this.cdRef.markForCheck();
    }
  }

  private sortFaqsByCategory(faqSearchResults: FaqSearchResult[]): Map<string, FaqEntry[]> {
    const result: Map<string, FaqEntry[]> = new Map();
    faqSearchResults?.forEach((faqSearchResult) => {
      faqSearchResult?.categories?.forEach((categoryList) => {
        const categories = categoryList.split('::');
        const category = categories[1];
        const subCategory = categories[2];
        const itemFaq = {
          ...faqSearchResult.faq,
          theme: { code: subCategory, name: subCategory }
        };
        if (!result.has(category)) {
          result.set(category, [itemFaq]);
        } else {
          const faqs = [...result.get(category), itemFaq];
          result.set(category, faqs);
        }
      });
    });
    return result;
  }
}
